import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Button } from '@/components/ui/button';
import { GlassCard } from '@/components/ui/glass-card';
import { FancySpinner } from '@/components/ui/spinner';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  TrendingUp, 
  Users, 
  MessageSquare, 
  ShieldAlert, 
  ArrowUpRight,
  Globe,
  Sparkles,
  Zap,
  Bot
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_users: 0,
    total_messages: 0,
    total_facts: 0,
    messages_today: 0,
    misinformation_count: 0,
    language_distribution: {} as Record<string, number>,
    topic_distribution: {} as Record<string, number>,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getAnalytics()
      .then(data => { setStats(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
        <FancySpinner size="xl" variant="gradient" />
        <p className="text-sm font-mono text-cyan-400 animate-pulse tracking-wider">
          Connecting to OLU Telemetry Engine...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-6"
      >
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-white via-slate-200 to-cyan-400 bg-clip-text text-transparent">
              OLU Command Center
            </h1>
            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_10px_rgba(6,182,212,0.3)]">
              Gabriel's AI Master
            </span>
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Real-time multi-channel telemetry, memory facts, and search intelligence
          </p>
        </div>

        <Button 
          onClick={() => navigate('/messages')} 
          className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold shadow-lg shadow-cyan-500/25 rounded-xl gap-2 transition-all hover:scale-105"
        >
          <Bot size={18} />
          Start AI Chat
          <ArrowUpRight size={16} />
        </Button>
      </motion.div>

      <GlassCard glow="cyan" className="p-8 border-cyan-500/30 relative overflow-hidden bg-gradient-to-r from-cyan-950/40 via-blue-950/40 to-slate-900/80">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 z-10 relative">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2 text-cyan-400 font-semibold text-sm">
              <Sparkles size={18} />
              <span>Unified Knowledge & Memory Data Hub</span>
            </div>
            <h2 className="text-2xl font-bold text-white">Inject Direct Memory & System Rules</h2>
            <p className="text-sm text-slate-300 leading-relaxed">
              Inject durable personal facts, custom instructions, or task guidelines directly into OLU. Updates persist immediately across WhatsApp and Web channels.
            </p>
          </div>
          <Button 
            onClick={() => navigate('/data')} 
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold px-6 py-6 rounded-xl shadow-lg shadow-cyan-500/30 transition-all hover:scale-105 whitespace-nowrap"
          >
            Open Data Hub
          </Button>
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { label: 'Active Users', value: stats.total_users.toLocaleString(), icon: <Users size={22} />, glow: 'cyan' as const, color: 'from-cyan-400 to-blue-500' },
          { label: 'Messages Processed', value: stats.total_messages.toLocaleString(), icon: <MessageSquare size={22} />, glow: 'purple' as const, color: 'from-purple-400 to-indigo-500' },
          { label: 'Verified Memory Facts', value: stats.total_facts.toString(), icon: <CheckCircle2 size={22} />, glow: 'emerald' as const, color: 'from-emerald-400 to-teal-500' },
          { label: 'Today\'s Queries', value: stats.messages_today.toLocaleString(), icon: <TrendingUp size={22} />, glow: 'amber' as const, color: 'from-amber-400 to-orange-500' },
        ].map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1, duration: 0.4 }}
          >
            <GlassCard glow={s.glow} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.label}</span>
                <div className={`p-2.5 rounded-xl bg-white/5 text-slate-200 border border-white/10`}>
                  {s.icon}
                </div>
              </div>
              <div className={`text-4xl font-extrabold bg-gradient-to-r ${s.color} bg-clip-text text-transparent`}>
                {s.value}
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard glow="cyan" className="p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Globe size={18} className="text-cyan-400" />
                Active Language Distribution
              </h3>
              <p className="text-xs text-slate-400">Supported languages: Hausa, Yoruba, Igbo, French, English</p>
            </div>
          </div>
          
          {Object.entries(stats.language_distribution).length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-sm">No language telemetry recorded</div>
          ) : (
            <div className="space-y-4">
              {Object.entries(stats.language_distribution).map(([lang, count]) => (
                <div key={lang} className="space-y-1.5">
                  <div className="flex justify-between text-xs font-medium">
                    <span className="text-slate-200 capitalize">{lang}</span>
                    <span className="text-cyan-400 font-mono">{count} messages</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                      style={{ width: `${Math.min(100, (count / (stats.total_messages || 1)) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>

        <GlassCard glow="purple" className="p-6 space-y-6">
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Zap size={18} className="text-purple-400" />
                Topic & Intent Classification
              </h3>
              <p className="text-xs text-slate-400">Intelligent intent routing analytics</p>
            </div>
          </div>

          {Object.entries(stats.topic_distribution).length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-sm">No topic telemetry recorded</div>
          ) : (
            <div className="space-y-3">
              {Object.entries(stats.topic_distribution).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([topic, count]) => (
                <div key={topic} className="flex justify-between items-center p-3 rounded-xl bg-white/5 border border-white/5">
                  <span className="text-sm font-medium text-slate-200 capitalize">{topic.replace(/_/g, ' ')}</span>
                  <span className="text-xs font-mono font-semibold text-purple-400 px-2.5 py-1 rounded-md bg-purple-500/10 border border-purple-500/20">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>

      <GlassCard glow="amber" className="p-6 border-red-500/30 bg-red-950/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-500/20 text-red-400 border border-red-500/30">
              <ShieldAlert size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Health Misinformation Flagged</h3>
              <p className="text-xs text-slate-400">Automated prebunking and evidence cross-referencing alerts</p>
            </div>
          </div>
          <div className="text-3xl font-extrabold text-red-400 font-mono">
            {stats.misinformation_count}
          </div>
        </div>
      </GlassCard>
    </div>
  );
}